import { useState, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { API_ENDPOINTS } from '../utils/apiConfig';

const useConflictHandling = (token, editingSession) => {
  const [conflicts, setConflicts] = useState([]);
  const debounceTimerRef = useRef(null);

  // ✅ PROTECTION: Hook ID unique
  const hookId = useRef(null);
  if (!hookId.current) {
    hookId.current = Math.random().toString(36).substr(2, 9);
    logger.debug('🔄 useConflictHandling - Initialisation UNIQUE:', hookId.current);
  }
  
  // ✅ PROTECTION: Éviter les rechargements
  const conflictLoadedRef = useRef(false);
  const debounceRef = useRef(null);
  
  // Vérifier les conflits de groupe et chef d'équipe
  const checkConflicts = useCallback(async (formData) => {
    // Support ancien et nouveau nommage
    const groupId = formData.group_id || formData.room_id;
    const teamLeaderId = formData.team_leader_id || formData.trainer_id;
    const prestationType = formData.prestation_type || formData.session_type;

    if (!formData.start_date || !formData.end_date || !groupId) {
      setConflicts([]);
      return;
    }

    try {
      const excludeParam = editingSession?.id ? `?exclude_session_id=${editingSession.id}` : '';

      // Appels en parallèle : disponibilité groupe + chef d'équipe
      const requests = [
        fetch(
          `${API_ENDPOINTS.GROUP_AVAILABILITY(formData.start_date)}${excludeParam}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        )
      ];

      // Vérification chef d'équipe via bulk-availability (route existante)
      if (prestationType === 'prestation' && teamLeaderId) {
        requests.push(
          fetch(API_ENDPOINTS.USERS_BULK_AVAILABILITY, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              user_ids: [teamLeaderId],
              date: formData.start_date,
              exclude_session_id: editingSession?.id
            })
          })
        );
      }

      const responses = await Promise.all(requests);
      const groupData = await responses[0].json();

      // Transformer la réponse bulk-availability en format attendu
      let teamLeaderData = { success: true, data: { is_available: true } };
      if (responses[1]) {
        const bulkData = await responses[1].json();
        if (bulkData.success && Array.isArray(bulkData.data)) {
          const leaderStatus = bulkData.data.find(u => u.user_id === teamLeaderId);
          teamLeaderData = {
            success: true,
            data: { is_available: leaderStatus?.is_available ?? true }
          };
        }
      }

      const detectedConflicts = [];

      // Vérifier conflits de groupe
      if (groupData.success && Array.isArray(groupData.data)) {
        const groupAvailability = groupData.data.find(g => g.id === groupId);
        if (groupAvailability && !groupAvailability.is_available) {
          detectedConflicts.push(`Groupe occupé: ${groupAvailability.prestation_number || groupAvailability.training_number}`);
        }
      }

      // Vérifier conflits de chef d'équipe
      if (teamLeaderData.success && teamLeaderId) {
        if (!teamLeaderData.data?.is_available) {
          detectedConflicts.push(`Chef d'équipe indisponible`);
        }
      }

      setConflicts(detectedConflicts);

    } catch (err) {
      logger.error('Erreur vérification conflits:', err);
      setConflicts([]);
    }
  }, [token, editingSession?.id]);

  // Vérifier les conflits avec debounce
  const checkConflictsDebounced = useCallback((formData) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      checkConflicts(formData);
      logger.debug('🔍 Vérification conflits (debounced):', formData.group_id || formData.room_id);
    }, 300);
  }, [checkConflicts]);

  // Vérifier les conflits de participants
  const checkParticipantConflicts = useCallback(async (participantIds, startDate, endDate) => {
    if (!participantIds || participantIds.length === 0 || !startDate || !endDate) {
      return [];
    }

    try {
      const response = await fetch(API_ENDPOINTS.PRESTATION_CHECK_CONFLICTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          participant_ids: participantIds,
          start_date: startDate,
          end_date: endDate,
          exclude_session_id: editingSession?.id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        return data.data || [];
      } else {
        logger.error('❌ Erreur API conflits participants:', data.error);
        return [];
      }
    } catch (error) {
      logger.error('❌ Erreur vérification conflits participants:', error);
      return [];
    }
  }, [token, editingSession?.id]);

  // Résoudre les conflits
  const resolveConflicts = useCallback(async (resolutions) => {
    try {
      const response = await fetch(API_ENDPOINTS.PRESTATION_RESOLVE_CONFLICTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolutions })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Erreur lors de la résolution des conflits');
      }
    } catch (error) {
      logger.error('❌ Erreur résolution conflits:', error);
      throw error;
    }
  }, [token]);

  // Nettoyer les timers
  const cleanup = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  return {
    conflicts,
    setConflicts,
    checkConflicts,
    checkConflictsDebounced,
    checkParticipantConflicts,
    resolveConflicts,
    cleanup
  };
};

export default useConflictHandling;